import {
  ApiResponseStatus,
  type IRemoteNotification,
  useFeedClient,
  useFeedData,
} from "@suprsend/react";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { Archive, ChevronDown, Mail, MailOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { env } from "@/config/env";

import { Loading } from "../../request/feedback-tool/feedbackTool/components/Loading";

const MAX_LINES = 5;

function formatActionLink(link: string) {
  return link.startsWith("http") || link.startsWith("/")
    ? link
    : `https://${link}`;
}

function getUrlPathname(url: string) {
  try {
    return new URL(url, env.VITE_BASE_URL).pathname;
  } catch {
    return;
  }
}

function FeedTabs() {
  const feedClient = useFeedClient();
  const feedData = useFeedData();

  const stores = feedClient?.feedOptions?.stores;

  if (!stores?.length) return null;

  return (
    <div className="sticky top-0 z-[2] mb-[10px] flex overflow-x-auto bg-bg-primary [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {stores.map((store) => {
        const isActive = feedData?.store?.storeId === store.storeId;
        const unreadCount = feedData?.meta?.[store.storeId] ?? 0;

        return (
          <button
            className={`flex cursor-pointer items-center gap-[5px] px-[15px] pb-[3px] pt-[5px] ${
              isActive ? "rounded-sm bg-bg-brand-primary-alt" : ""
            }`}
            key={store.storeId}
            onClick={() => feedClient?.changeActiveStore(store.storeId)}
          >
            <span
              className={`text-sm font-semibold ${
                isActive ? "text-text-brand-secondary" : "text-text-quaternary"
              }`}
            >
              {store.label}
            </span>
            {unreadCount > 0 && (
              <span className="flex h-[18px] w-[20px] items-center justify-center rounded-full bg-[var(--color-text-brand-secondary)]">
                <span
                  className={`font-semibold leading-none text-white ${
                    unreadCount > 99 ? "text-[8px]" : "text-[10px]"
                  }`}
                >
                  {unreadCount}
                </span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function NotificationItem({
  notification,
  onPress,
  onSeen,
}: {
  notification: IRemoteNotification;
  onPress: () => void;
  onSeen: (notificationId: string) => void;
}) {
  const feedClient = useFeedClient();
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef<HTMLButtonElement>(null);

  const lines = notification.message.text
    .split("\n")
    .filter((line) => line.trim() !== "");
  const hasMore = lines.length > MAX_LINES;
  const collapsedLines = lines.slice(0, MAX_LINES);
  const extraLines = lines.slice(MAX_LINES);

  useEffect(() => {
    if (notification.seen_on || !cardRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          onSeen(notification.n_id);
          observer.disconnect();
        }
      },
      { threshold: 1 },
    );

    observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, [notification.n_id, notification.seen_on, onSeen]);

  return (
    <button
      className="py-2 px-4 flex flex-row justify-between hover:bg-bg-tertiary cursor-pointer w-full"
      onClick={onPress}
      ref={cardRef}
    >
      <div className="text-start w-full">
        <p
          className={`${notification.read_on ? "text-text-tertiary" : "text-fg-brand-primary-alt"} text-base font-medium`}
        >
          {notification.message.header}
        </p>
        <ul className="list-disc list-inside text-text-tertiary font-normal leading-normal">
          <p dangerouslySetInnerHTML={{ __html: collapsedLines[0] ?? "" }} />
          {collapsedLines.slice(1).map((line, index) => (
            <li className="pl-4" key={index}>
              {line.replace(/^- /, "")}{" "}
            </li>
          ))}
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                initial={{ height: 0, opacity: 0 }}
                style={{ overflow: "hidden" }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
              >
                {extraLines.map((line, index) => (
                  <li className="pl-4" key={index}>
                    {line.replace(/^- /, "")}{" "}
                  </li>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </ul>
        {hasMore && (
          <button
            className="mt-1 flex items-center gap-1 text-xs text-fg-brand-primary-alt hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            <motion.span
              animate={{ rotate: expanded ? 180 : 0 }}
              className="flex items-center gap-1"
              transition={{ duration: 0.2 }}
            >
              <ChevronDown size={13} />
            </motion.span>
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>
      <div className="">
        <p className="text-sm font-normal text-text-tertiary">
          {dayjs(notification.created_on).fromNow()}
        </p>
        <div className="flex flex-row justify-end items-center">
          {notification.read_on ? (
            <MailOpen
              className="text-text-tertiary cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                feedClient?.markAsUnread(notification.n_id);
              }}
              size={17}
            />
          ) : (
            <Mail
              className="text-fg-brand-primary-alt cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                feedClient?.markAsRead(notification.n_id);
              }}
              size={17}
            />
          )}
          <Archive
            className={`${notification.archived ? "text-fg-brand-primary-alt" : "text-text-tertiary cursor-pointer"}  ml-1`}
            onClick={(e) => {
              e.stopPropagation();
              if (!notification.archived) {
                feedClient?.markAsArchived(notification.n_id);
              }
            }}
            size={17}
          />
        </div>
      </div>
    </button>
  );
}

export function NotificationFeed() {
  const feedClient = useFeedClient();
  const feedData = useFeedData();
  const [unseenIds, setUnseenIds] = useState<string[]>([]);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const apiStatus = feedData?.apiStatus;
  const notifications = feedData?.notifications ?? [];
  const hasMore = feedData?.pageInfo?.hasMore ?? false;

  useEffect(() => {
    if (unseenIds.length === 0 || !feedClient) return;

    const timer = setTimeout(() => {
      feedClient.markBulkAsSeen(unseenIds);
      setUnseenIds([]);
    }, 1000);

    return () => clearTimeout(timer);
  }, [unseenIds, feedClient]);

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!sentinel || !hasMore || apiStatus !== ApiResponseStatus.SUCCESS) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        feedClient?.fetchNextPage();
      }
    });

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [feedClient, hasMore, apiStatus]);

  const handleSeen = (notificationId: string) => {
    setUnseenIds((prev) =>
      prev.includes(notificationId) ? prev : [...prev, notificationId],
    );
  };

  const handleNotificationPress = (notification: IRemoteNotification) => {
    feedClient?.markAsRead(notification.n_id);
    feedClient?.markAsInteracted(notification.n_id);

    const url = notification.message?.url;

    if (!url) return;

    const pathname = getUrlPathname(url);

    if (pathname) {
      for (const item of feedData?.notifications ?? []) {
        if (
          item.n_id !== notification.n_id &&
          !item.read_on &&
          item.message?.url?.includes(pathname)
        ) {
          feedClient?.markAsRead(item.n_id);
        }
      }
    }

    window.open(
      formatActionLink(url),
      notification.message?.open_in_new_tab ? "_blank" : "_self",
    );
  };

  return (
    <div className="w-full bg-bg-primary">
      <FeedTabs />

      {(!feedData || apiStatus === ApiResponseStatus.LOADING) && <Loading />}

      {(apiStatus === ApiResponseStatus.ERROR ||
        (apiStatus === ApiResponseStatus.SUCCESS &&
          notifications.length === 0)) && (
        <div className="mx-8 mt-10 flex flex-col items-center justify-center">
          <p className="mb-4 mt-5 text-center text-base font-semibold text-text-primary">
            No notifications yet
          </p>
          <p className="text-center text-sm text-text-tertiary">
            We&apos;ll let you know when we&apos;ve got something new for you.
          </p>
        </div>
      )}

      {notifications.length > 0 && (
        <div>
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.n_id}
              notification={notification}
              onPress={() => handleNotificationPress(notification)}
              onSeen={handleSeen}
            />
          ))}
          {apiStatus === ApiResponseStatus.FETCHING_MORE && <Loading />}
          <div ref={sentinelRef} />
        </div>
      )}
    </div>
  );
}
